import { z } from "zod";
import {
    BillingIntervalUnitSchema,
    BillingModeSchema,
    BillingProviderSubscriptionStatusSchema,
    PlanBillingOptionSummarySchema,
    PlanInvitationStatusSchema,
} from "./expert-billing";

export const ExpertPlanSummarySchema = z.object({
    id: z.number().int(),
    name: z.string(),
    slug: z.string(),
});

export const ExpertPlanTypeSchema = z.enum(["freelancer", "agency", "all"]);

const HttpUrlSchema = z.string().url().refine((value) => {
    try {
        const protocol = new URL(value).protocol;
        return protocol === "http:" || protocol === "https:";
    } catch {
        return false;
    }
}, "Expected an HTTP(S) URL");

// Plan-level billing_cycle/price_cents/currency are deprecated compatibility
// projections of the canonical month×1 billing option (backend D-058).
export const ExpertPlanSchema = ExpertPlanSummarySchema.extend({
    expert_type: ExpertPlanTypeSchema.nullable(),
    billing_cycle: z.string(),
    price_cents: z.number().int(),
    currency: z.string(),
    billing_options: z.array(PlanBillingOptionSummarySchema).optional(),
    is_founding: z.boolean(),
    is_active: z.boolean(),
    subscriptions_count: z.number().int().nonnegative(),
});

export const ExpertPlanListResponseSchema = z.object({
    default_term_days: z.number().int().positive(),
    plans: z.array(ExpertPlanSchema),
});

export const CreateExpertPlanRequestSchema = z.object({
    name: z.string().min(1),
    slug: z.string().min(1),
    expert_type: ExpertPlanTypeSchema,
    billing_cycle: z.string().min(1).optional(),
    price_cents: z.number().int().optional(),
    currency: z.string().optional(),
    is_founding: z.boolean().optional(),
    is_active: z.boolean().optional(),
});

export const UpdateExpertPlanRequestSchema = CreateExpertPlanRequestSchema.partial();

export const ExpertPlanMutationResultSchema = ExpertPlanSchema.partial().extend({
    id: z.number().int(),
});

export const ExpertSubscriptionInvoiceSchema = z.object({
    id: z.number().int(),
    subscription_id: z.number().int().optional(),
    v2_plan_id: z.number().int().optional(),
    invoice_type: z.string().optional(),
    invoice_url: HttpUrlSchema.nullable().optional(),
    provider: z.string().nullable().optional(),
    provider_status: z.string().nullable().optional(),
    amount_cents: z.number().int().optional(),
    currency: z.string().optional(),
    term_days_override: z.number().int().positive().nullable().optional(),
    discount_cents: z.number().int().nullable().optional(),
    discount_note: z.string().nullable().optional(),
    status: z.string().optional(),
    issued_at: z.string().nullable().optional(),
    paid_at: z.string().nullable().optional(),
    service_period_starts_at: z.string().nullable().optional(),
    service_period_ends_at: z.string().nullable().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
});

export const ExpertSubscriptionRecentInvoiceSchema = ExpertSubscriptionInvoiceSchema.extend({
    invoice_type: z.string(),
    amount_cents: z.number().int(),
    status: z.string(),
});

export const AdminExpertProviderInvitationSummarySchema = z.object({
    id: z.number().int(),
    status: PlanInvitationStatusSchema.optional(),
});

export const AdminExpertProviderSubscriptionSchema = z.object({
    id: z.number().int(),
    provider: z.string(),
    mode: BillingModeSchema,
    provider_subscription_id: z.string(),
    status: BillingProviderSubscriptionStatusSchema,
    cancel_at_period_end: z.boolean(),
    current_period_starts_at: z.string().nullable(),
    current_period_ends_at: z.string().nullable(),
    trial_ends_at: z.string().nullable(),
    cancel_at: z.string().nullable(),
    canceled_at: z.string().nullable(),
    provider_state_updated_at: z.string().nullable().optional(),
    plan: ExpertPlanSummarySchema.nullable().optional(),
    billing_option: z.object({
        id: z.number().int(),
        label: z.string(),
        interval_unit: BillingIntervalUnitSchema,
        interval_count: z.number().int().positive(),
        amount_cents: z.number().int().nonnegative(),
        currency: z.string(),
    }).nullable().optional(),
    invitation: AdminExpertProviderInvitationSummarySchema.nullable().optional(),
    invitation_id: z.number().int().nullable().optional(),
});

export const ExpertSubscriptionSchema = z.object({
    id: z.number().int(),
    expert_id: z.number().int(),
    v2_plan_id: z.number().int(),
    status: z.string(),
    starts_at: z.string().nullable(),
    ends_at: z.string().nullable(),
    renews_at: z.string().nullable(),
    cancelled_at: z.string().nullable().optional(),
    invoices_count: z.number().int().nonnegative(),
    // Mutation responses predate the admin resource wrapper, so defaults keep
    // those manual-only responses compatible while list responses carry the
    // explicit managed marker from backend PR #113.
    is_managed: z.boolean().optional().default(false),
    provider_subscription: AdminExpertProviderSubscriptionSchema.nullable().optional().default(null),
    plan: ExpertPlanSummarySchema,
    recent_invoices: z.array(ExpertSubscriptionRecentInvoiceSchema).optional(),
});

export const RegisterExpertSubscriptionInvoiceRequestSchema = z.object({
    v2_plan_id: z.number().int(),
    invoice_type: z.literal("external"),
    invoice_url: HttpUrlSchema.optional(),
    amount_cents: z.number().int(),
    currency: z.string().optional(),
    term_days_override: z.number().int().positive().nullable().optional(),
    discount_cents: z.number().int().optional(),
    discount_note: z.string().optional(),
    issued_at: z.string(),
    paid_at: z.string(),
});

export const RegisterExpertSubscriptionInvoiceResponseSchema = z.object({
    transition: z.string(),
    invoice: ExpertSubscriptionInvoiceSchema.extend({
        subscription_id: z.number().int(),
        v2_plan_id: z.number().int(),
        invoice_type: z.string(),
        status: z.string(),
    }),
    subscription: ExpertSubscriptionSchema,
});

export const ListExpertSubscriptionInvoicesItemSchema = ExpertSubscriptionInvoiceSchema.extend({
    subscription_id: z.number().int(),
    v2_plan_id: z.number().int(),
    invoice_type: z.string(),
    amount_cents: z.number().int(),
    status: z.string(),
});

export const UpdateExpertSubscriptionInvoiceRequestSchema = z.object({
    invoice_url: HttpUrlSchema.optional(),
    amount_cents: z.number().int().optional(),
    currency: z.string().optional(),
    term_days_override: z.number().int().positive().nullable().optional(),
    discount_cents: z.number().int().nullable().optional(),
    discount_note: z.string().nullable().optional(),
    issued_at: z.string().nullable().optional(),
    paid_at: z.string().nullable().optional(),
    status: z.string().optional(),
});

export type ExpertPlanSummary = z.infer<typeof ExpertPlanSummarySchema>;
export type ExpertPlanType = z.infer<typeof ExpertPlanTypeSchema>;
export type ExpertPlan = z.infer<typeof ExpertPlanSchema>;
export type ExpertPlanListResponse = z.infer<typeof ExpertPlanListResponseSchema>;
export type CreateExpertPlanRequest = z.infer<typeof CreateExpertPlanRequestSchema>;
export type UpdateExpertPlanRequest = z.infer<typeof UpdateExpertPlanRequestSchema>;
export type ExpertPlanMutationResult = z.infer<typeof ExpertPlanMutationResultSchema>;
export type ExpertSubscriptionInvoice = z.infer<typeof ExpertSubscriptionInvoiceSchema>;
export type ExpertSubscriptionRecentInvoice = z.infer<typeof ExpertSubscriptionRecentInvoiceSchema>;
export type AdminExpertProviderInvitationSummary = z.infer<typeof AdminExpertProviderInvitationSummarySchema>;
export type AdminExpertProviderSubscription = z.infer<typeof AdminExpertProviderSubscriptionSchema>;
export type ExpertSubscription = z.infer<typeof ExpertSubscriptionSchema>;
export type RegisterExpertSubscriptionInvoiceRequest = z.infer<typeof RegisterExpertSubscriptionInvoiceRequestSchema>;
export type RegisterExpertSubscriptionInvoiceResponse = z.infer<typeof RegisterExpertSubscriptionInvoiceResponseSchema>;
export type ListExpertSubscriptionInvoicesItem = z.infer<typeof ListExpertSubscriptionInvoicesItemSchema>;
export type UpdateExpertSubscriptionInvoiceRequest = z.infer<typeof UpdateExpertSubscriptionInvoiceRequestSchema>;
